import React, { useState } from 'react';
import { Phone, MessageCircle, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { useToast } from '../hooks/use-toast';
import { academyInfo } from '../data/mockData';

const INITIAL_FORM = { name: '', phone: '', course: '', message: '' };

const validateForm = (form) => {
  if (!form.name || !form.phone || !form.course) {
    return { ok: false, title: 'Missing Information', description: 'Please fill in all required fields' };
  }
  if (form.phone.length !== 10 || !/^\d+$/.test(form.phone)) {
    return { ok: false, title: 'Invalid Phone Number', description: 'Please enter a valid 10-digit phone number' };
  }
  return { ok: true };
};

const submitEnquiry = async (form) => {
  const apiUrl = process.env.REACT_APP_BACKEND_URL;
  if (!apiUrl) throw new Error('Backend URL not configured');

  const response = await fetch(`${apiUrl}/api/enquiries`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(form),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.detail || 'Failed to submit enquiry');
  return data;
};

const EnquiryForm = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCourseChange = (value) => {
    setFormData((prev) => ({ ...prev, course: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const v = validateForm(formData);
    if (!v.ok) {
      toast({ title: v.title, description: v.description, variant: 'destructive' });
      return;
    }

    setIsSubmitting(true);
    try {
      const data = await submitEnquiry(formData);
      toast({
        title: 'Enquiry Submitted!',
        description: data.message || "We'll contact you shortly. Thank you for your interest!",
      });
      setFormData(INITIAL_FORM);
    } catch (error) {
      console.error('Enquiry submission error:', error);
      toast({
        title: 'Submission Failed',
        description: 'Unable to submit enquiry. Please call or WhatsApp us directly.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCall = () => {
    window.location.href = `tel:${academyInfo.phone}`;
  };

  const handleWhatsApp = () => {
    window.open(
      `https://wa.me/91${academyInfo.whatsapp}?text=Hi, I want to enquire about stock market courses`,
      '_blank',
    );
  };

  return (
    <section className="py-16 sm:py-20 bg-gradient-to-br from-green-50 via-white to-green-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Card className="border-2 border-green-600 shadow-2xl bg-white">
            <CardHeader className="text-center bg-gradient-to-r from-green-50 to-white">
              <CardTitle className="text-3xl sm:text-4xl font-bold text-gray-900">
                Ready to Start Your <span className="text-green-600">Trading Journey?</span>
              </CardTitle>
              <p className="text-lg text-gray-600 mt-2">Fill the form below or contact us directly</p>
            </CardHeader>
            <CardContent className="p-6 sm:p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <FormFields
                  formData={formData}
                  onInputChange={handleInputChange}
                  onCourseChange={handleCourseChange}
                />
                <SubmitButton isSubmitting={isSubmitting} />
              </form>

              <Divider />
              <DirectContactRow onCall={handleCall} onWhatsApp={handleWhatsApp} />
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

const FormFields = ({ formData, onInputChange, onCourseChange }) => (
  <>
    <FieldWrapper id="name" label="Your Name" required>
      <Input
        id="name"
        name="name"
        type="text"
        placeholder="Enter your full name"
        value={formData.name}
        onChange={onInputChange}
        required
        className="border-2 border-gray-300 focus:border-green-600"
      />
    </FieldWrapper>

    <FieldWrapper id="phone" label="Phone Number" required>
      <Input
        id="phone"
        name="phone"
        type="tel"
        placeholder="Enter 10-digit mobile number"
        value={formData.phone}
        onChange={onInputChange}
        maxLength="10"
        required
        className="border-2 border-gray-300 focus:border-green-600"
      />
    </FieldWrapper>

    <FieldWrapper id="course" label="Course Interest" required>
      <Select value={formData.course} onValueChange={onCourseChange}>
        <SelectTrigger className="border-2 border-gray-300 focus:border-green-600">
          <SelectValue placeholder="Select a course" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="technical-analysis">Advanced Technical Analysis</SelectItem>
          <SelectItem value="options-trading">Options Trading Mastery</SelectItem>
          <SelectItem value="both">Both Courses</SelectItem>
          <SelectItem value="consultation">Need Guidance / Consultation</SelectItem>
        </SelectContent>
      </Select>
    </FieldWrapper>

    <FieldWrapper id="message" label="Additional Message (Optional)">
      <Textarea
        id="message"
        name="message"
        placeholder="Any specific questions or requirements..."
        value={formData.message}
        onChange={onInputChange}
        rows="4"
        className="border-2 border-gray-300 focus:border-green-600"
      />
    </FieldWrapper>
  </>
);

const FieldWrapper = ({ id, label, required, children }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-semibold text-gray-700 mb-2">
      {label} {required && <span className="text-red-600">*</span>}
    </label>
    {children}
  </div>
);

const SubmitButton = ({ isSubmitting }) => (
  <Button
    type="submit"
    disabled={isSubmitting}
    className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
    data-testid="enquiry-submit-btn"
  >
    {isSubmitting ? (
      <>Submitting...</>
    ) : (
      <>
        <Send className="mr-2 h-5 w-5" />
        Submit Enquiry
      </>
    )}
  </Button>
);

const Divider = () => (
  <div className="relative my-8">
    <div className="absolute inset-0 flex items-center">
      <div className="w-full border-t border-gray-300"></div>
    </div>
    <div className="relative flex justify-center text-sm">
      <span className="px-4 bg-white text-gray-600 font-semibold">OR CONTACT DIRECTLY</span>
    </div>
  </div>
);

const DirectContactRow = ({ onCall, onWhatsApp }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
    <Button
      onClick={onCall}
      variant="outline"
      className="border-2 border-green-600 text-green-700 hover:bg-green-50 py-6 text-base font-semibold"
    >
      <Phone className="mr-2 h-5 w-5" />
      Call: {academyInfo.phone}
    </Button>
    <Button
      onClick={onWhatsApp}
      variant="outline"
      className="border-2 border-green-600 text-green-700 hover:bg-green-50 py-6 text-base font-semibold"
    >
      <MessageCircle className="mr-2 h-5 w-5" />
      WhatsApp Now
    </Button>
  </div>
);

export default EnquiryForm;
